import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type BankTransactionStatus =
  | "imported"
  | "unmatched"
  | "matched"
  | "ignored"
  | "not_applicable";

export interface BankTransaction {
  id: string;
  bankAccountId: string | null;
  associationId: number;
  externalId: string;
  bookingDate: string;
  valueDate: string;
  amount: number;
  currency: string;
  creditorName: string | null;
  debtorName: string | null;
  referenceNumber: string | null;
  remittanceInfo: string | null;
  importedAt: string;
  status: BankTransactionStatus;
  ignoreReason: string | null;
  bankAccount?: { id: string } | null;
}

interface BankTransactionsState {
  transactions: BankTransaction[];
  unmatchedCount: number;
  status: "idle" | "loading" | "failed";
}

const initialState: BankTransactionsState = {
  transactions: [],
  unmatchedCount: 0,
  status: "idle",
};

export const fetchBankTransactions = createAsyncThunk(
  "bankTransactions/fetchAll",
  async ({
    associationId,
    status,
    fromDate,
    toDate,
    bankAccountId,
  }: {
    associationId: number;
    status?: BankTransactionStatus;
    fromDate?: string;
    toDate?: string;
    bankAccountId?: string;
  }) => {
    const params: Record<string, string> = {
      associationId: String(associationId),
    };
    if (status) params.status = status;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (bankAccountId) params.bankAccountId = bankAccountId;
    const response = await axiosInstance.get("/bank-transactions", { params });
    return response.data as BankTransaction[];
  }
);

export const fetchUnmatched = createAsyncThunk(
  "bankTransactions/fetchUnmatched",
  async (associationId: number) => {
    const response = await axiosInstance.get("/bank-transactions/unmatched", {
      params: { associationId },
    });
    const count = Number(response.headers["x-total-count"] ?? 0);
    return { transactions: response.data as BankTransaction[], count };
  }
);

export const manualReconcile = createAsyncThunk(
  "bankTransactions/reconcile",
  async ({
    id,
    associationId,
    invoiceId,
    amount,
  }: {
    id: string;
    associationId: number;
    invoiceId: number;
    amount?: number;
  }) => {
    const response = await axiosInstance.post(
      `/bank-transactions/${id}/reconcile`,
      { invoiceId, amount },
      { params: { associationId } }
    );
    return { id, result: response.data };
  }
);

export const ignoreBankTransaction = createAsyncThunk(
  "bankTransactions/ignore",
  async ({
    id,
    associationId,
    reason,
  }: {
    id: string;
    associationId: number;
    reason?: string;
  }) => {
    await axiosInstance.put(
      `/bank-transactions/${id}/ignore`,
      { reason },
      { params: { associationId } }
    );
    return id;
  }
);

export const unignoreBankTransaction = createAsyncThunk(
  "bankTransactions/unignore",
  async ({ id, associationId }: { id: string; associationId: number }) => {
    await axiosInstance.put(
      `/bank-transactions/${id}/unignore`,
      {},
      { params: { associationId } }
    );
    return id;
  }
);

const bankTransactionsSlice = createSlice({
  name: "bankTransactions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankTransactions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBankTransactions.fulfilled, (state, action) => {
        state.status = "idle";
        state.transactions = action.payload;
      })
      .addCase(fetchBankTransactions.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchUnmatched.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUnmatched.fulfilled, (state, action) => {
        state.status = "idle";
        state.transactions = action.payload.transactions;
        state.unmatchedCount = action.payload.count;
      })
      .addCase(fetchUnmatched.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(manualReconcile.fulfilled, (state, action) => {
        const tx = state.transactions.find((t) => t.id === action.payload.id);
        if (tx) tx.status = "matched";
        state.unmatchedCount = Math.max(0, state.unmatchedCount - 1);
      })
      .addCase(ignoreBankTransaction.fulfilled, (state, action) => {
        const tx = state.transactions.find((t) => t.id === action.payload);
        if (tx) tx.status = "ignored";
        state.unmatchedCount = Math.max(0, state.unmatchedCount - 1);
      })
      .addCase(unignoreBankTransaction.fulfilled, (state, action) => {
        const tx = state.transactions.find((t) => t.id === action.payload);
        if (tx) tx.status = "unmatched";
      });
  },
});

export default bankTransactionsSlice.reducer;
